// PageDescription: FAQ 상세 페이지
import { dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import React from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import FaqDetailTemplate from '@/components/templates/communication/Faq/FaqDetailTemplate';
import { SIX_HOURS_IN_MILLISECONDS } from '@/constants/common';
import useAppInstallTopBanner from '@/hooks/Cuj/useAppInstallTopBanner';
import useFaqDetailViewModel from '@/hooks/faq/useFaqDetailViewModel';
import useGetRelatedFaq from '@/hooks/faq/useGetRelatedFaq';
import useGetMagazineDetail from '@/hooks/magazine/useGetMagazineDetail';
import useGetPrevNextMagazinePid from '@/hooks/magazine/useGetPrevNextMagazinePid';
import useGetMagazineRelatedQna from '@/hooks/qna/useGetMagazineRelatedQna';
import { ExtendedQueryClient } from '@/models/common/query-client';
import { ssrQueryCache } from '@/models/common/ssr-cache';
import { createFaqLdJson } from '@/parsers/Communication/magazine';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { GraphqlError, ServerError } from '@/types/error';
import { MagazineType } from '@/types/magazine';

interface Props extends CustomPageProps {
  id: string;
}

const FaqDetail = ({ id, client }: Props) => {
  const viewModel = useFaqDetailViewModel({ faqPid: id, client });
  const topBannerViewModel = useAppInstallTopBanner();
  const ldJson = createFaqLdJson(viewModel.faq);

  return (
    <>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson ?? {}) }} />
      </Head>
      <NextSeo
        title={`${viewModel.faq?.title} - 닥터나우`}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.FAQ.DETAIL.path}/${id}`}
        description={viewModel.faq?.description}
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.FAQ.DETAIL.path}/${id}`,
          title: viewModel.faq?.title || '',
          description: viewModel.faq?.description || '',
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_MAGAZINE_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
      <FaqDetailTemplate viewModel={viewModel} topBannerViewModel={topBannerViewModel} client={client} />
    </>
  );
};

FaqDetail.Layout = DefaultLayout;
export default FaqDetail;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const id = ctx.params?.id as string;

  if (!id) {
    return {
      redirect: {
        destination: '/content/faq',
        permanent: false,
      },
    };
  }

  const queryClient = new ExtendedQueryClient();

  const cacheKey = ctx.req.url as string;

  try {
    if (ssrQueryCache.has(cacheKey)) {
      queryClient.setQueryDataFromCache(ssrQueryCache.get(cacheKey) ?? []);

      return {
        props: {
          id,
          dehydratedState: dehydrate(queryClient),
        },
      };
    }

    await Promise.allSettled([
      useGetMagazineDetail.prefetchQuery(queryClient, id),
      useGetPrevNextMagazinePid.prefetchQuery(queryClient, {
        magazinePid: id,
        contentType: MagazineType.FAQ,
      }),
      useGetRelatedFaq.prefetchQuery(queryClient, id),
      useGetMagazineRelatedQna.prefetchQuery(queryClient, id),
    ]);

    ssrQueryCache.set(
      cacheKey,
      queryClient.getQueryClientData([
        useGetMagazineDetail.getKey(id),
        useGetPrevNextMagazinePid.getKey({ magazinePid: id, contentType: MagazineType.FAQ }),
        useGetRelatedFaq.getKey(id),
        useGetMagazineRelatedQna.getKey(id),
      ]),
      SIX_HOURS_IN_MILLISECONDS
    );

    return {
      props: {
        id,
        dehydratedState: dehydrate(queryClient),
      },
    };
  } catch (e) {
    const { code } = e as unknown as GraphqlError;
    if (code === ServerError.NotFound) {
      return {
        notFound: true,
      };
    }
    if (ctx.res && code === ServerError.BadRequest) {
      ctx.res.statusCode = 400;
    }

    return {
      notFound: true,
    };
  }
};
